import React, { useState, useMemo, ReactNode, CSSProperties, useEffect, Dispatch, SetStateAction, useRef } from 'react';
import { classNames } from 'primereact/utils';
import { getNestedValue } from 'utils/object';
import { Paginator, PaginatorPageState } from 'primereact/paginator';
import { Tooltip } from 'components/_new/Tooltip/Tooltip';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { universalRenderer } from 'utils/universalRenderer';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon';
import { Loader } from '../Loader';

export type TableColumn<T = any> = {
	label: string;
	renderLabel?: (label: string) => ReactNode;
	min?: boolean;
	longLabel?: string;
	name: string;
	select?: string;
	body?: (data: T) => ReactNode;
	onCellClick?: (data: T) => void;
	sortable?: boolean;
	bodyStyle?: CSSProperties;
	bodyClassName?: string;
	isDate?: boolean;
};

type TableProps<T> = {
	keyOptionProp?: string;
	data: T[];
	columns: TableColumn<T>[];
	defaultSort?: {
		field: keyof T | string;
		order: 'asc' | 'desc';
	};
	onSort?: (field: keyof T | string, order: 'desc' | 'asc' | null) => void;
	pagination?: {
		itemsPerPage: number;
		total: number;
		current: number;
		onPageChange: (page: number) => void;
	};
	isRefetching?: boolean;
	selectedState?: [number[], Dispatch<SetStateAction<number[]>>];
	renderBottomRight?: ReactNode;
	loading?: boolean;
	title?: string;
	subtitle?: string;
	fullscreenEnabled?: boolean;
	headerStyle?: CSSProperties;
	headerClassName?: string;
};

type SortType<T> = { field: keyof T | string | null; order: 'desc' | 'asc' | null };

type TableContentProps<T> = {
	inModal?: boolean;
	data: T[];
	columns: TableColumn<T>[];
	currentSort: SortType<T>;
	handleSort: (column: keyof T | string, order: 'desc' | 'asc' | null) => void;
	isRefetching?: boolean;
	loading: boolean;
	withCheckboxColumn: boolean;
	selectedState?: [number[], Dispatch<SetStateAction<number[]>>];
	allSelectableIds: number[];
	isAllSelected: boolean;
	handleSelectAll: () => void;
	finalKeyOptionProp: string;
	pagination?: {
		itemsPerPage: number;
		total: number;
		current: number;
		onPageChange: (page: number) => void;
	};
	renderBottomRight?: ReactNode;
	t: (key: string, options?: any) => string;
};

const TableContent = <T,>({
	inModal = false,
	data,
	columns,
	currentSort,
	handleSort,
	isRefetching,
	loading,
	withCheckboxColumn,
	selectedState,
	allSelectableIds,
	isAllSelected,
	handleSelectAll,
	finalKeyOptionProp,
	pagination,
	renderBottomRight,
	t,
}: TableContentProps<T>) => {
	const selectAllCheckboxRef = useRef<HTMLInputElement>(null);

	const isSomeSelected = useMemo(() => {
		if (!selectedState || allSelectableIds.length === 0) return false;
		const selectedCount = allSelectableIds.filter((id) => selectedState[0].includes(id)).length;
		return selectedCount > 0 && selectedCount < allSelectableIds.length;
	}, [selectedState, allSelectableIds]);

	// Ustawianie stanu indeterminate
	useEffect(() => {
		if (selectAllCheckboxRef.current) {
			selectAllCheckboxRef.current.indeterminate = isSomeSelected;
		}
	}, [isSomeSelected]);

	const handlePageChange = ({ page }: PaginatorPageState) => {
		if (pagination?.onPageChange) {
			pagination.onPageChange(page + 1);
		}
	};

	return (
		<div className={classNames('flex-1 h-full overflow-y-auto', { 'fullscreen-table-container': inModal })}>
			<div className={classNames('w-full overflow-x-auto', { 'fullscreen-scroll': inModal })}>
				<table
					className={classNames('p-datatable w-full', {
						'opacity-70 pointer-events-none': isRefetching,
					})}
				>
					<thead className="p-datatable-thead">
						<tr>
							{withCheckboxColumn && (
								<th className="p-0" style={{ width: '40px' }}>
									<div className="flex justify-content-center align-items-center p-2">
										<Checkbox
											inputRef={selectAllCheckboxRef}
											checked={isAllSelected}
											onChange={handleSelectAll}
											disabled={allSelectableIds.length === 0}
											tooltip={isAllSelected ? t('actions.deselectAll') : t('actions.selectAll')}
											tooltipOptions={{ position: 'top' }}
										/>
									</div>
								</th>
							)}
							{columns.map(({ name, label, min, longLabel, sortable }) => {
								const currentSorted = currentSort?.field === name;
								return (
									<th
										key={name}
										className={classNames('p-0', {
											'p-sortable-column': sortable,
											'p-min-column': min,
										})}
										onClick={(event) => {
											if (!sortable) {
												event.preventDefault();
												return;
											}
											const ascing = currentSort?.order === 'asc' ? 'desc' : 'asc';
											handleSort(name as keyof T, ascing);
										}}
									>
										{longLabel ? (
											<Tooltip content={longLabel} target={`#header-${name}`} position="top" />
										) : null}
										<div
											id={`header-${name}`}
											className="flex flex-row gap-2 align-items-center p-2"
										>
											<span>{label}</span>
											{sortable ? (
												<Icon
													iconSet="fa"
													iconStyle="solid"
													name={currentSort?.order === 'asc' ? 'caret-up' : 'caret-down'}
													className={currentSorted ? 'text-primary' : undefined}
												/>
											) : (
												<span />
											)}
										</div>
									</th>
								);
							})}
						</tr>
					</thead>
					{!loading && (
						<tbody className="p-datatable-tbody">
							{data.map((dataItem) => {
								const trKey = String((dataItem as any)?.[finalKeyOptionProp]);
								const idValue: number | null = (dataItem as any)?.id || null;
								return (
									<tr key={trKey} className="p-1">
										{withCheckboxColumn && selectedState && (
											<td>
												<div className="flex justify-content-center align-items-center">
													<Checkbox
														checked={idValue && selectedState?.[0].includes(idValue)}
														onChange={({ checked }) => {
															if (idValue) {
																selectedState[1]?.((prev) => {
																	return checked
																		? [...prev, idValue]
																		: prev.filter((id) => id !== idValue);
																});
															}
														}}
														disabled={idValue === null}
													/>
												</div>
											</td>
										)}
										{columns.map(
											({
												name,
												select,
												min,
												body,
												bodyStyle,
												bodyClassName,
												isDate = false,
												onCellClick,
											}) => {
												const tdKey = `${trKey}-${name}`;
												const rawVal = getNestedValue(dataItem, (select || name) as any);
												let val;
												if (body) {
													val = body(dataItem);
												} else {
													val = rawVal;
													const isEmpty =
														typeof val === 'undefined' ||
														(typeof val === 'string' && val.trim() === '');
													if (isEmpty) {
														val = t('table.noData');
													}

													// for dates
													else if (val instanceof Date) {
														val = val.toLocaleString();
													}

													// if is date
													else if (isDate) {
														val =
															rawVal && new Date(val).toString() !== 'Invalid Date'
																? new Date(val).toLocaleString()
																: 'Invalid Date';
													}
												}

												return (
													<td
														key={tdKey}
														style={bodyStyle}
														className={classNames(
															{
																'p-clickable-cell': Boolean(onCellClick),
																'p-min-column': min,
															},
															bodyClassName
														)}
														onClick={(event) => {
															if (!onCellClick) {
																event.preventDefault();
																return;
															}
															onCellClick(dataItem);
														}}
													>
														{val}
													</td>
												);
											}
										)}
									</tr>
								);
							})}
						</tbody>
					)}
				</table>
			</div>

			{loading && (
				<div className="flex flex-row gap-2 align-items-center justify-content-center lg:justify-content-between flex-wrap">
					<Loader text={t('table.loading')} />
				</div>
			)}

			<div className="flex flex-row gap-2 align-items-center justify-content-center lg:justify-content-between flex-wrap">
				{selectedState && selectedState[0]?.length > 0 ? (
					<div className="flex-order-2 lg:flex-order-0 flex-0 p-2 nowrap">
						{t('misc.selected', { count: selectedState[0]?.length })}
					</div>
				) : (
					<div className="flex-order-0" />
				)}
				{pagination && (
					<Paginator
						first={pagination.current * pagination.itemsPerPage - pagination.itemsPerPage + 1}
						rows={pagination.itemsPerPage}
						totalRecords={pagination.total}
						onPageChange={handlePageChange}
						className="flex-order-1 lg:flex-order-1 w-full lg:w-auto"
					/>
				)}
				{renderBottomRight ? (
					<div className="flex-order-3">{universalRenderer(renderBottomRight)}</div>
				) : (
					<div className="flex-order-3" />
				)}
			</div>
		</div>
	);
};

const tableStyles = `
.table-wrapper {
   position: relative;
   background: white;
   border: 1px solid #e5e7eb;
   border-radius: 8px;
   overflow: hidden;
}

.floating-fullscreen-btn {
   position: fixed !important;
   right: 20px !important;
   bottom: 20px !important;
   z-index: 9999 !important;
   background: #6366f1 !important;
   border: 2px solid #ffffff !important;
   color: white !important;
   width: 50px !important;
   height: 50px !important;
   border-radius: 8px !important;
   display: flex !important;
   align-items: center !important;
   justify-content: center !important;
   transition: all 0.2s ease !important;
   cursor: pointer !important;
   box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
   font-size: 18px !important;
   padding: 4rem;
}

.floating-fullscreen-btn:enabled:hover {
   background: #4f46e5 !important;
   color: white !important;
   transform: scale(1.05) !important;
   box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2) !important;
}

.floating-fullscreen-btn:focus {
   box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.3), 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.floating-exit-fullscreen-btn {
   position: fixed !important;
   right: 20px !important;
   bottom: 20px !important;
   z-index: 10000 !important;
   background: #ef4444 !important;
   border: 2px solid #ffffff !important;
   color: white !important;
   width: 50px !important;
   height: 50px !important;
   border-radius: 8px !important;
   display: flex !important;
   align-items: center !important;
   justify-content: center !important;
   transition: all 0.2s ease !important;
   cursor: pointer !important;
   box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
   font-size: 18px !important;
}

.floating-exit-fullscreen-btn:enabled:hover {
   background: #dc2626 !important;
   color: white !important;
   transform: scale(1.05) !important;
   box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2) !important;
}

.floating-exit-fullscreen-btn:focus {
   box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.3), 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.floating-fullscreen-btn.entering,
.floating-exit-fullscreen-btn.entering {
   animation: slideIn 0.3s ease-out forwards;
}

.floating-fullscreen-btn.exiting,
.floating-exit-fullscreen-btn.exiting {
   animation: slideOut 0.3s ease-in forwards;
}

@keyframes slideIn {
   from {
      transform: translateY(100px);
      opacity: 0;
   }
   to {
      transform: translateY(0);
      opacity: 1;
   }
}

@keyframes slideOut {
   from {
      transform: translateY(0);
      opacity: 1;
   }
   to {
      transform: translateY(100px);
      opacity: 0;
   }
}

.p-dialog-content {
   padding: 0 !important;
}

.p-dialog-header {
   background: #f8fafc !important;
   color: #1e293b !important;
   padding: 1.5rem !important;
   border-bottom: 1px solid #e2e8f0 !important;
   z-index: 2;
}

.p-dialog-header-icon {
   color: #64748b !important;
   margin-left; 2rem;
}

.p-dialog-header .p-dialog-title {
   color: #1e293b !important;
}

.fullscreen-table-container {
   height: 100vh;
   display: flex;
   flex-direction: column;
}

.fullscreen-scroll {
   flex: 1;
   overflow-y: auto;
   max-height: 100vh;
}

.fullscreen-scroll .p-datatable {
   max-height: 70vh;
}

.fullscreen-header-content {
   display: flex;
   justify-content: space-between;
   align-items: center;
   color: #1e293b;
}

.fullscreen-header-left h4 {
   margin: 0 0 0.25rem 0;
   font-size: 1.25rem;
   font-weight: 600;
   color: #1e293b;
}

.fullscreen-header-left small {
   font-size: 0.875rem;
   color: #64748b;
}

.fullscreen-header-right {
   font-size: 0.875rem;
   color: #64748b;
   text-align: right;
   margin-right: 2rem;
}

@media (max-width: 768px) {
   .floating-fullscreen-btn,
   .floating-exit-fullscreen-btn {
      right: 16px !important;
      bottom: 16px !important;
      width: 46px !important;
      height: 46px !important;
      font-size: 16px !important;
   }

   .fullscreen-header-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.5rem;
   }

   .fullscreen-header-right {
      align-self: stretch;
      text-align: left;
   }
}
`;

export const Table = <T extends Record<string, any> = any>(props: TableProps<T>) => {
	const {
		keyOptionProp,
		data,
		columns,
		defaultSort,
		onSort,
		pagination,
		isRefetching,
		selectedState,
		renderBottomRight,
		loading = false,
		title,
		subtitle,
		fullscreenEnabled = false,
		headerStyle,
		headerClassName,
	} = props;
	const { t } = useTranslation();
	const finalKeyOptionProp = keyOptionProp || 'id';
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [showFullscreenBtn, setShowFullscreenBtn] = useState(false);
	const tableWrapperRef = useRef<HTMLDivElement>(null);

	const firstSortableColumnIdx = columns.findIndex(({ sortable }) => sortable);
	const inbuildSort: SortType<T> =
		firstSortableColumnIdx !== -1
			? {
					field: columns[firstSortableColumnIdx].name as keyof T,
					order: 'asc',
			  }
			: { field: null, order: null };
	const [currentSort, setCurrentSort] = useState<SortType<T>>(defaultSort || inbuildSort);

	// Logika dla "zaznacz wszystkie"
	const allSelectableIds = useMemo(() => {
		return data
			.map((item) => (item as any)?.[finalKeyOptionProp])
			.filter((id) => id !== null && id !== undefined)
			.map(Number);
	}, [data, finalKeyOptionProp]);

	const isAllSelected = useMemo(() => {
		if (!selectedState || allSelectableIds.length === 0) return false;
		return allSelectableIds.every((id) => selectedState[0].includes(id));
	}, [selectedState, allSelectableIds]);

	const handleSelectAll = () => {
		if (!selectedState) return;

		const [selectedIds, setSelectedIds] = selectedState;

		if (isAllSelected) {
			setSelectedIds((prev) => prev.filter((id) => !allSelectableIds.includes(id)));
		} else {
			setSelectedIds((prev) => {
				const newIds = allSelectableIds.filter((id) => !prev.includes(id));
				return [...prev, ...newIds];
			});
		}
	};

	const handleSort = (column: keyof T | string, order: 'desc' | 'asc' | null) => {
		if (onSort) {
			onSort(column, order);
		} else {
			setCurrentSort({ field: column, order });
		}
	};

	useEffect(() => {
		if (onSort && defaultSort) {
			setCurrentSort(defaultSort);
		}
	}, [defaultSort, onSort]);

	useEffect(() => {
		let observer: IntersectionObserver;

		if (fullscreenEnabled && tableWrapperRef.current) {
			observer = new IntersectionObserver(
				([entry]) => {
					setShowFullscreenBtn(entry.isIntersecting);
				},
				{
					threshold: 0.1,
					rootMargin: '-50px',
				}
			);

			observer.observe(tableWrapperRef.current);
		}

		return () => {
			observer?.disconnect();
		};
	}, [fullscreenEnabled]);

	const withCheckboxColumn = Boolean(selectedState);

	const fullscreenHeader = (
		<div className="fullscreen-header-content">
			<div className="fullscreen-header-left">
				{title && <h4>{t('table.fullscreenTitle', { title })}</h4>}
				{subtitle && <small>{subtitle}</small>}
			</div>
			{pagination && (
				<div className="fullscreen-header-right">
					<div>
						{t('table.pageInfo', {
							current: pagination.current,
							total: Math.ceil(pagination.total / pagination.itemsPerPage),
						})}
					</div>
					<div
						dangerouslySetInnerHTML={{
							__html: t('table.totalRecords', { total: pagination.total }),
						}}
					/>
				</div>
			)}
		</div>
	);

	return (
		<>
			<style dangerouslySetInnerHTML={{ __html: tableStyles }} />

			<div ref={tableWrapperRef} className={classNames('table-wrapper', headerClassName)} style={headerStyle}>
				<TableContent
					data={data}
					columns={columns}
					currentSort={currentSort}
					handleSort={handleSort}
					isRefetching={isRefetching}
					loading={loading}
					withCheckboxColumn={withCheckboxColumn}
					selectedState={selectedState}
					allSelectableIds={allSelectableIds}
					isAllSelected={isAllSelected}
					handleSelectAll={handleSelectAll}
					finalKeyOptionProp={finalKeyOptionProp}
					pagination={pagination}
					renderBottomRight={renderBottomRight}
					t={t}
				/>
			</div>

			{fullscreenEnabled && (showFullscreenBtn || isFullscreen) && (
				<Button
					icon={isFullscreen ? 'pi pi-window-minimize' : 'pi pi-external-link'}
					className={classNames(
						isFullscreen ? 'floating-exit-fullscreen-btn' : 'floating-fullscreen-btn',
						'p-button-rounded entering p-4 m-3'
					)}
					tooltip={isFullscreen ? t('table.exitFullscreen') : t('table.fullscreenMode')}
					tooltipOptions={{ position: 'top' }}
					onClick={() => setIsFullscreen(!isFullscreen)}
					aria-label={isFullscreen ? t('table.exitFullscreen') : t('table.openFullscreen')}
				/>
			)}
			{fullscreenEnabled && (
				<Dialog
					visible={isFullscreen}
					style={{ width: '100vw', height: '100vh' }}
					contentStyle={{ height: '100%', padding: 0 }}
					header={fullscreenHeader}
					modal
					closable
					onHide={() => setIsFullscreen(false)}
					maximized
					blockScroll
					focusOnShow
					aria-label={t('table.tableInFullscreen')}
				>
					<TableContent
						inModal
						data={data}
						columns={columns}
						currentSort={currentSort}
						handleSort={handleSort}
						isRefetching={isRefetching}
						loading={loading}
						withCheckboxColumn={withCheckboxColumn}
						selectedState={selectedState}
						allSelectableIds={allSelectableIds}
						isAllSelected={isAllSelected}
						handleSelectAll={handleSelectAll}
						finalKeyOptionProp={finalKeyOptionProp}
						pagination={pagination}
						renderBottomRight={renderBottomRight}
						t={t}
					/>
				</Dialog>
			)}
		</>
	);
};
